import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Divider,
  Grid,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Search as SearchIcon,
  QrCode as QrCodeIcon,
  CalendarToday as CalendarIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { useVenue } from '../../contexts/VenueContext';
import { useUser } from '../../contexts/UserContext';
import BookingService, { PackageBooking } from '../../services/bookingService';
import { Venue } from '../../types/Venue';

// Import the components instead of lazy loading them
import VenueBookingsTable from './components/VenueBookingsTable';
import QrCodeScannerDialog from './components/QrCodeScannerDialog';
import VenueSelector from './components/VenueSelector';

const VenueBookingsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { venues } = useVenue();
  const { currentUser } = useUser();
  
  const [venuesLoading, setVenuesLoading] = useState(true);
  const [selectedVenue, setSelectedVenue] = useState<Venue | null>(null);
  const [bookings, setBookings] = useState<PackageBooking[]>([]);
  const [filteredBookings, setFilteredBookings] = useState<PackageBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [dateRange, setDateRange] = useState<{
    startDate: string;
    endDate: string;
  }>({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  });

  useEffect(() => {
    if (venues.length > 0) {
      setVenuesLoading(false);
    }
  }, [venues]);

  // Load bookings for the selected venue
  useEffect(() => {
    const fetchBookings = async () => {
      if (!selectedVenue || !selectedVenue.id) {
        setBookings([]);
        setFilteredBookings([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const venueBookings = await BookingService.getVenueBookings(selectedVenue.id);
        setBookings(venueBookings);
        
        // Apply current filters
        filterBookings(venueBookings, searchTerm, tabValue);
      } catch (error) {
        console.error('Error fetching bookings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [selectedVenue]);

  // Filter bookings based on search term and tab
  const filterBookings = (
    bookingsToFilter: PackageBooking[], 
    search: string, 
    tab: number
  ) => {
    // First filter by status (tab)
    let filtered = bookingsToFilter;
    
    if (tab === 0) { // Upcoming
      filtered = bookingsToFilter.filter(booking => {
        if (!booking.bookingDate) return false;
        const date = booking.bookingDate.toDate();
        return booking.status === 'confirmed' && date >= new Date();
      });
    } else if (tab === 1) { // Today
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);
      
      filtered = bookingsToFilter.filter(booking => {
        if (!booking.bookingDate) return false;
        const date = booking.bookingDate.toDate();
        return booking.status === 'confirmed' && date >= today && date < tomorrow;
      });
    } else if (tab === 2) { // Completed
      filtered = bookingsToFilter.filter(booking => 
        booking.status === 'completed'
      );
    } else if (tab === 3) { // Cancelled
      filtered = bookingsToFilter.filter(booking => 
        booking.status === 'cancelled' || booking.status === 'refunded'
      );
    }
    
    // Then filter by search term
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(booking => 
        booking.packageName.toLowerCase().includes(searchLower) ||
        booking.userName.toLowerCase().includes(searchLower) ||
        booking.userEmail.toLowerCase().includes(searchLower) ||
        booking.redemptionCode.toLowerCase().includes(searchLower) ||
        booking.id.toLowerCase().includes(searchLower)
      );
    }
    
    setFilteredBookings(filtered);
  };

  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const search = event.target.value;
    setSearchTerm(search);
    filterBookings(bookings, search, tabValue);
  };

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    filterBookings(bookings, searchTerm, newValue);
  };

  // Handle venue change
  const handleVenueChange = (venue: Venue) => {
    setSelectedVenue(venue);
  };

  // Handle QR dialog open/close
  const handleQrDialogOpen = () => {
    setQrDialogOpen(true);
  };

  const handleQrDialogClose = () => {
    setQrDialogOpen(false);
  };

  // Handle redemption code entry
  const handleRedemptionCodeEntry = async (code: string): Promise<{
    success: boolean;
    booking?: PackageBooking;
    error?: string;
  }> => {
    if (!selectedVenue || !selectedVenue.id) {
      return {
        success: false,
        error: 'No venue selected'
      };
    }
    
    try {
      const result = await BookingService.verifyRedemptionCode(selectedVenue.id, code);
      
      if (result.valid && result.booking) {
        await BookingService.redeemBooking(result.booking.id);
        
        // Refresh bookings after redemption
        const updatedBookings = await BookingService.getVenueBookings(selectedVenue.id);
        setBookings(updatedBookings);
        filterBookings(updatedBookings, searchTerm, tabValue);
        
        return {
          success: true,
          booking: result.booking
        };
      } else {
        return {
          success: false,
          error: 'Invalid redemption code or booking not found.'
        };
      }
    } catch (error) {
      console.error('Error redeeming code:', error);
      return {
        success: false,
        error: 'Failed to redeem code. Please try again.'
      };
    }
  };

  return (
     <Container maxWidth='lg' sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
           <Typography variant='h4' component='h1'>
              Manage Bookings
           </Typography>

           <Button
              variant='contained'
              color='primary'
              startIcon={<QrCodeIcon />}
              onClick={handleQrDialogOpen}
           >
              Scan QR Code
           </Button>
        </Box>

        {/* Venue selector */}
        <Paper sx={{ p: 3, mb: 3 }}>
           <Typography variant='subtitle1' gutterBottom>
              Select Venue
           </Typography>
           <VenueSelector
              venues={venues}
              selectedVenue={selectedVenue}
              onVenueChange={handleVenueChange}
              loading={venuesLoading}
           />
        </Paper>

        {selectedVenue ? (
           <>
              {/* Search and filter section */}
              <Box sx={{ mb: 3 }}>
                 <Grid container spacing={2} alignItems='center'>
                    <Grid item xs={12} md={6}>
                       <TextField
                          fullWidth
                          placeholder='Search by name, email, or redemption code'
                          value={searchTerm}
                          onChange={handleSearchChange}
                          InputProps={{
                             startAdornment: (
                                <InputAdornment position='start'>
                                   <SearchIcon />
                                </InputAdornment>
                             ),
                          }}
                          variant='outlined'
                          size='small'
                       />
                    </Grid>
                    <Grid item xs={12} md={6}>
                       <Box sx={{ display: 'flex', gap: 2 }}>
                          <TextField
                             label='From'
                             type='date'
                             value={dateRange.startDate}
                             onChange={(e) =>
                                setDateRange({ ...dateRange, startDate: e.target.value })
                             }
                             InputLabelProps={{ shrink: true }}
                             size='small'
                             sx={{ flexGrow: 1 }}
                          />
                          <TextField
                             label='To'
                             type='date'
                             value={dateRange.endDate}
                             onChange={(e) =>
                                setDateRange({ ...dateRange, endDate: e.target.value })
                             }
                             InputLabelProps={{ shrink: true }}
                             size='small'
                             sx={{ flexGrow: 1 }}
                          />
                       </Box>
                    </Grid>
                 </Grid>
              </Box>

              {/* Tabs section */}
              <Paper sx={{ mb: 3 }}>
                 <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    variant={isMobile ? 'scrollable' : 'standard'}
                    scrollButtons={isMobile ? 'auto' : undefined}
                    centered={!isMobile}
                 >
                    <Tab label='Upcoming' />
                    <Tab label='Today' />
                    <Tab label='Completed' />
                    <Tab label='Cancelled' />
                 </Tabs>
              </Paper>

              {/* Bookings table */}
              <VenueBookingsTable
                 bookings={filteredBookings}
                 loading={loading}
                 onRedeemBooking={handleRedemptionCodeEntry}
                 venueId={selectedVenue.id}
              />
           </>
        ) : (
           <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant='h6' gutterBottom>
                 Select a Venue to View Bookings
              </Typography>
              <Typography variant='body1' color='text.secondary'>
                 Choose a venue from the dropdown above to manage its bookings.
              </Typography>
           </Paper>
        )}

        {/* QR Code Scanner Dialog */}
        {/* <QrCodeScannerDialog
        open={qrDialogOpen}
        onClose={handleQrDialogClose}
        onCodeScanned={handleRedemptionCodeEntry}
        venueName={selectedVenue?.name || ''}
      /> */}
     </Container>
  );
};

export default VenueBookingsPage;
